<script setup>
/* eslint-disable vue/html-closing-bracket-newline, vue/multiline-html-element-content-newline */
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import { useAlert } from 'dashboard/composables';
import { useDealsStore } from 'dashboard/stores/deals';
import BaseSettingsHeader from 'dashboard/routes/dashboard/settings/components/BaseSettingsHeader.vue';
import SettingsLayout from 'dashboard/routes/dashboard/settings/SettingsLayout.vue';

const store = useDealsStore();
const { t } = useI18n();
const activeStages = ref([]);
const archivedStages = ref([]);
const expandedStages = ref([]);
const selectedTemplate = ref('');
const isLoading = ref(false);
const isSaving = ref(false);
const archiveCandidate = ref(null);
const replacementStageId = ref('');
const templateConfirmOpen = ref(false);
let clientSequence = 0;

const palette = [
  '#3B82F6',
  '#6366F1',
  '#8B5CF6',
  '#F59E0B',
  '#14B8A6',
  '#22C55E',
  '#EF4444',
];
const templates = computed(() => [
  {
    id: 'general_crm',
    label: t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.GENERAL_CRM'),
    stages: [
      [
        t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.STAGES.PROSPECT'),
        'open',
        'needs_analysis',
      ],
      [
        t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.STAGES.QUALIFICATION'),
        'open',
        'study',
      ],
      [
        t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.STAGES.PROPOSAL'),
        'open',
        'quotation',
      ],
      [
        t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.STAGES.NEGOTIATION'),
        'open',
        'order_purchase',
      ],
      [
        t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.STAGES.WON_CLOSING'),
        'won',
        'won',
      ],
      [
        t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.STAGES.LOST_CLOSING'),
        'lost',
        'lost',
      ],
    ],
  },
  {
    id: 'b2b_services',
    label: t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.B2B_SERVICES'),
    stages: [
      [
        t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.STAGES.NEW_LEAD'),
        'open',
        'needs_analysis',
      ],
      [
        t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.STAGES.DIAGNOSIS'),
        'open',
        'study',
      ],
      [
        t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.STAGES.TECHNICAL_PROPOSAL'),
        'open',
        'quotation',
      ],
      [
        t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.STAGES.APPROVAL'),
        'open',
        'order_purchase',
      ],
      [
        t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.STAGES.IMPLEMENTATION'),
        'open',
        'invoicing',
      ],
      [
        t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.STAGES.ACTIVE_CLIENT'),
        'won',
        'won',
      ],
      [
        t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.STAGES.NOT_VIABLE'),
        'lost',
        'lost',
      ],
    ],
  },
  {
    id: 'quotation_sales',
    label: t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.QUOTATION_SALES'),
    stages: [
      [
        t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.STAGES.NEED'),
        'open',
        'needs_analysis',
      ],
      [
        t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.STAGES.STUDY'),
        'open',
        'study',
      ],
      [
        t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.STAGES.QUOTATION'),
        'open',
        'quotation',
      ],
      [
        t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.STAGES.ORDER_CONTRACT'),
        'open',
        'order_purchase',
      ],
      [
        t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.STAGES.INVOICING'),
        'open',
        'invoicing',
      ],
      [t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.STAGES.WON'), 'won', 'won'],
      [t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.STAGES.LOST'), 'lost', 'lost'],
    ],
  },
  {
    id: 'after_sales',
    label: t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.AFTER_SALES'),
    stages: [
      [
        t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.STAGES.REQUEST'),
        'open',
        'needs_analysis',
      ],
      [
        t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.STAGES.EVALUATION'),
        'open',
        'study',
      ],
      [
        t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.STAGES.IN_PROGRESS'),
        'open',
        'quotation',
      ],
      [
        t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.STAGES.WAITING_CUSTOMER'),
        'open',
        'order_purchase',
      ],
      [
        t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.STAGES.RESOLVED'),
        'won',
        'won',
      ],
      [
        t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.STAGES.CLOSED_UNRESOLVED'),
        'lost',
        'lost',
      ],
    ],
  },
]);

const toggleFields = computed(() => [
  {
    key: 'allowDragDrop',
    label: t('COMMERCIAL.PIPELINE_SETTINGS.FIELDS.ALLOW_DRAG_DROP'),
  },
  {
    key: 'requiresConfirmation',
    label: t('COMMERCIAL.PIPELINE_SETTINGS.FIELDS.REQUIRES_CONFIRMATION'),
  },
  {
    key: 'requiresLossReason',
    label: t('COMMERCIAL.PIPELINE_SETTINGS.FIELDS.REQUIRES_LOSS_REASON'),
  },
]);
const validationMessages = computed(() => {
  const messages = [];
  if (activeStages.value.some(stage => !stage.name.trim()))
    messages.push(t('COMMERCIAL.PIPELINE_SETTINGS.VALIDATION.NAME'));
  if (!activeStages.value.some(stage => stage.stageType === 'open'))
    messages.push(t('COMMERCIAL.PIPELINE_SETTINGS.VALIDATION.OPEN'));
  if (
    activeStages.value.filter(stage => stage.stageType === 'won').length !== 1
  )
    messages.push(t('COMMERCIAL.PIPELINE_SETTINGS.VALIDATION.WON'));
  if (
    activeStages.value.filter(stage => stage.stageType === 'lost').length !== 1
  )
    messages.push(t('COMMERCIAL.PIPELINE_SETTINGS.VALIDATION.LOST'));
  return messages;
});
const stageTypeLabel = type =>
  ({
    open: t('COMMERCIAL.PIPELINE_SETTINGS.TYPES.OPEN'),
    won: t('COMMERCIAL.PIPELINE_SETTINGS.TYPES.WON'),
    lost: t('COMMERCIAL.PIPELINE_SETTINGS.TYPES.LOST'),
  })[type];
const replacementOptions = computed(() =>
  activeStages.value.filter(
    stage =>
      stage !== archiveCandidate.value &&
      stage.stageType === archiveCandidate.value?.stageType
  )
);

const normalizeRules = rules => ({
  followUpDays: rules?.followUpDays ?? null,
  inactivityAlertDays: rules?.inactivityAlertDays ?? null,
  suggestedAction: rules?.suggestedAction ?? '',
  aiLabel: rules?.aiLabel ?? '',
  intent: rules?.intent ?? '',
});
const defaultOpenRules = () => ({
  followUpDays: 2,
  inactivityAlertDays: 7,
  suggestedAction: t('COMMERCIAL.PIPELINE_SETTINGS.DEFAULTS.SUGGESTED_ACTION'),
  aiLabel: 'commercial_follow_up',
  intent: 'follow_up',
});
const templateRules = (stageType, intent) => ({
  followUpDays: 2,
  inactivityAlertDays: 7,
  suggestedAction:
    stageType === 'open'
      ? t('COMMERCIAL.PIPELINE_SETTINGS.DEFAULTS.SUGGESTED_ACTION')
      : t('COMMERCIAL.PIPELINE_SETTINGS.DEFAULTS.TERMINAL_ACTION'),
  aiLabel: `commercial_${intent}`,
  intent,
});
const stageRulesSummary = (stage, position) => {
  const followUp = stage.rules.followUpDays;
  const inactivity = stage.rules.inactivityAlertDays;
  const base = t('COMMERCIAL.PIPELINE_SETTINGS.SUMMARY', {
    position,
    type: stageTypeLabel(stage.stageType),
  });

  if (followUp == null && inactivity == null) {
    return `${base} · ${t('COMMERCIAL.PIPELINE_SETTINGS.SUMMARY_NO_RULES')}`;
  }

  const followUpSummary =
    followUp == null
      ? t('COMMERCIAL.PIPELINE_SETTINGS.SUMMARY_FOLLOW_UP_EMPTY')
      : t('COMMERCIAL.PIPELINE_SETTINGS.SUMMARY_FOLLOW_UP', {
          days: followUp,
        });
  const inactivitySummary =
    inactivity == null
      ? t('COMMERCIAL.PIPELINE_SETTINGS.SUMMARY_INACTIVITY_EMPTY')
      : t('COMMERCIAL.PIPELINE_SETTINGS.SUMMARY_INACTIVITY', {
          days: inactivity,
        });

  return `${base} · ${followUpSummary} · ${inactivitySummary}`;
};
const stageKey = stage => stage.id || stage.clientId;
const normalizeStage = stage => {
  clientSequence += 1;
  return {
    ...stage,
    clientId: stage.clientId || `stage-${clientSequence}`,
    active: stage.active !== false,
    replacementStageId: stage.replacementStageId || null,
    rules: normalizeRules(stage.rules),
  };
};
const createStageDraft = (attributes = {}) =>
  normalizeStage({
    name: '',
    position: activeStages.value.length + 1,
    color: palette[activeStages.value.length % palette.length],
    stageType: 'open',
    allowDragDrop: true,
    requiresConfirmation: false,
    requiresLossReason: false,
    dealsCount: 0,
    rules: defaultOpenRules(),
    ...attributes,
  });
const updatePositions = () => {
  activeStages.value.forEach((stage, index) => {
    stage.position = index + 1;
  });
};
const isExpanded = stage => expandedStages.value.includes(stageKey(stage));
const toggleStage = stage => {
  const key = stageKey(stage);
  expandedStages.value = isExpanded(stage)
    ? expandedStages.value.filter(item => item !== key)
    : [...expandedStages.value, key];
};

const setStages = value => {
  const normalized = value.map(normalizeStage);
  activeStages.value = normalized.filter(stage => stage.active);
  archivedStages.value = normalized.filter(stage => !stage.active);
  updatePositions();
};
const loadConfiguration = async () => {
  isLoading.value = true;
  try {
    setStages(await store.fetchPipelineConfiguration());
  } catch {
    useAlert(t('COMMERCIAL.PIPELINE_SETTINGS.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};
const addStage = () => {
  const stage = createStageDraft();
  activeStages.value.push(stage);
  expandedStages.value.push(stageKey(stage));
  updatePositions();
};
const duplicateStage = source => {
  const index = activeStages.value.indexOf(source);
  const stage = createStageDraft({
    ...source,
    id: undefined,
    key: undefined,
    name: t('COMMERCIAL.PIPELINE_SETTINGS.COPY_NAME', { name: source.name }),
    dealsCount: 0,
    rules: { ...source.rules },
  });
  activeStages.value.splice(index + 1, 0, stage);
  expandedStages.value.push(stageKey(stage));
  updatePositions();
};
const requestArchive = stage => {
  archiveCandidate.value = stage;
  replacementStageId.value = '';
};
const confirmArchive = () => {
  const stage = archiveCandidate.value;
  if (!stage) return;
  if (!stage.id) {
    activeStages.value = activeStages.value.filter(item => item !== stage);
    archiveCandidate.value = null;
    updatePositions();
    return;
  }
  stage.active = false;
  stage.replacementStageId = replacementStageId.value || null;
  activeStages.value = activeStages.value.filter(item => item !== stage);
  archivedStages.value.push(stage);
  archiveCandidate.value = null;
  updatePositions();
};
const restoreStage = stage => {
  stage.active = true;
  stage.replacementStageId = null;
  archivedStages.value = archivedStages.value.filter(item => item !== stage);
  activeStages.value.push(stage);
  updatePositions();
};
const requestTemplate = () => {
  if (selectedTemplate.value) templateConfirmOpen.value = true;
};
const applyTemplate = () => {
  const template = templates.value.find(
    item => item.id === selectedTemplate.value
  );
  if (!template) return;
  const existingByType = type =>
    activeStages.value.filter(stage => stage.stageType === type);
  const used = new Set();
  template.stages.forEach(([name, stageType, intent], index) => {
    const existing = existingByType(stageType).find(stage => !used.has(stage));
    const attributes = {
      name,
      stageType,
      color: palette[index % palette.length],
      rules: templateRules(stageType, intent),
      requiresConfirmation: stageType !== 'open',
      requiresLossReason: stageType === 'lost',
    };
    if (existing) {
      Object.assign(existing, attributes);
      used.add(existing);
    } else {
      const stage = createStageDraft(attributes);
      activeStages.value.push(stage);
      used.add(stage);
    }
  });
  activeStages.value = [
    ...used,
    ...activeStages.value.filter(stage => !used.has(stage)),
  ];
  updatePositions();
  templateConfirmOpen.value = false;
  useAlert(t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.APPLIED'));
};
const saveConfiguration = async () => {
  if (validationMessages.value.length) {
    useAlert(validationMessages.value[0]);
    return;
  }
  isSaving.value = true;
  updatePositions();
  try {
    setStages(
      await store.updatePipelineConfiguration([
        ...activeStages.value,
        ...archivedStages.value,
      ])
    );
    useAlert(t('COMMERCIAL.PIPELINE_SETTINGS.SAVED'));
  } catch {
    useAlert(t('COMMERCIAL.PIPELINE_SETTINGS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

onMounted(loadConfiguration);
</script>

<template>
  <SettingsLayout :is-loading="isLoading">
    <template #header>
      <BaseSettingsHeader
        :title="t('COMMERCIAL.PIPELINE_SETTINGS.TITLE')"
        :description="t('COMMERCIAL.PIPELINE_SETTINGS.DESCRIPTION')"
      >
        <template #actions>
          <div class="flex flex-wrap gap-2">
            <button
              type="button"
              class="rounded-lg border border-n-weak px-3 py-2 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2"
              @click="addStage"
            >
              <span
                class="i-lucide-plus me-1 inline-block size-4 align-middle"
              />{{ t('COMMERCIAL.PIPELINE_SETTINGS.ADD_STAGE') }}
            </button>
            <button
              type="button"
              class="rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="isLoading || isSaving"
              @click="saveConfiguration"
            >
              {{ t('COMMERCIAL.PIPELINE_SETTINGS.SAVE') }}
            </button>
          </div>
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <section class="mb-5 rounded-xl border border-n-weak bg-n-solid-2 p-4">
        <div class="flex flex-col gap-3 md:flex-row md:items-end">
          <label class="min-w-0 flex-1 text-sm text-n-slate-11"
            >{{ t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.LABEL')
            }}<select
              v-model="selectedTemplate"
              class="mt-1.5 h-10 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-n-slate-12"
            >
              <option value="">
                {{ t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.PLACEHOLDER') }}
              </option>
              <option
                v-for="template in templates"
                :key="template.id"
                :value="template.id"
              >
                {{ template.label }}
              </option>
            </select></label
          >
          <button
            type="button"
            class="h-10 rounded-lg border border-n-weak px-4 text-sm font-medium hover:bg-n-alpha-2 disabled:opacity-50"
            :disabled="!selectedTemplate"
            @click="requestTemplate"
          >
            {{ t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.APPLY') }}
          </button>
        </div>
      </section>

      <div
        v-if="validationMessages.length"
        class="mb-4 rounded-xl border border-n-amber-6 bg-n-amber-3 p-4 text-sm text-n-amber-11"
      >
        <p class="font-medium">
          {{ t('COMMERCIAL.PIPELINE_SETTINGS.VALIDATION.TITLE') }}
        </p>
        <ul class="mt-2 list-disc space-y-1 ps-5">
          <li v-for="message in validationMessages" :key="message">
            {{ message }}
          </li>
        </ul>
      </div>
      <p class="mb-3 text-sm text-n-slate-10">
        {{ t('COMMERCIAL.PIPELINE_SETTINGS.DRAG_HINT') }}
      </p>

      <Draggable
        v-model="activeStages"
        item-key="clientId"
        handle=".stage-drag-handle"
        class="flex flex-col gap-3"
        ghost-class="opacity-40"
        @end="updatePositions"
      >
        <template #item="{ element: stage, index }">
          <article class="rounded-xl border border-n-weak bg-n-background">
            <header class="flex items-center gap-3 p-4">
              <button
                type="button"
                class="stage-drag-handle cursor-grab rounded-md p-1.5 text-n-slate-9 hover:bg-n-alpha-2"
              >
                <span class="i-lucide-grip-vertical block size-5" />
              </button>
              <button
                type="button"
                class="flex min-w-0 flex-1 items-center gap-3 text-start"
                @click="toggleStage(stage)"
              >
                <span
                  class="shrink-0 rounded bg-n-alpha-2 px-1.5 py-1 font-mono text-[10px] text-n-slate-9"
                  >{{ stage.color }}</span
                ><span class="min-w-0 flex-1"
                  ><span class="block truncate font-semibold text-n-slate-12">{{
                    stage.name || t('COMMERCIAL.PIPELINE_SETTINGS.NEW_STAGE')
                  }}</span
                  ><span class="mt-0.5 block text-xs text-n-slate-9">{{
                    stageRulesSummary(stage, index + 1)
                  }}</span></span
                ><span
                  class="size-4 text-n-slate-9"
                  :class="
                    isExpanded(stage)
                      ? 'i-lucide-chevron-up'
                      : 'i-lucide-chevron-down'
                  "
                />
              </button>
              <button
                type="button"
                class="rounded-lg p-2 text-n-slate-9 hover:bg-n-alpha-2"
                :title="t('COMMERCIAL.PIPELINE_SETTINGS.DUPLICATE')"
                @click="duplicateStage(stage)"
              >
                <span class="i-lucide-copy size-4" />
              </button>
              <button
                type="button"
                class="rounded-lg p-2 text-n-ruby-10 hover:bg-n-ruby-3"
                :title="t('COMMERCIAL.PIPELINE_SETTINGS.ARCHIVE')"
                @click="requestArchive(stage)"
              >
                <span class="i-lucide-archive size-4" />
              </button>
            </header>

            <div v-if="isExpanded(stage)" class="border-t border-n-weak p-4">
              <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                <label class="flex flex-col gap-1.5 text-sm text-n-slate-11"
                  >{{ t('COMMERCIAL.PIPELINE_SETTINGS.FIELDS.NAME')
                  }}<input
                    v-model="stage.name"
                    required
                    class="h-10 rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-n-slate-12"
                /></label>
                <label class="flex flex-col gap-1.5 text-sm text-n-slate-11"
                  >{{ t('COMMERCIAL.PIPELINE_SETTINGS.FIELDS.TYPE')
                  }}<select
                    v-model="stage.stageType"
                    class="h-10 rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-n-slate-12"
                  >
                    <option value="open">
                      {{ t('COMMERCIAL.PIPELINE_SETTINGS.TYPES.OPEN') }}
                    </option>
                    <option value="won">
                      {{ t('COMMERCIAL.PIPELINE_SETTINGS.TYPES.WON') }}
                    </option>
                    <option value="lost">
                      {{ t('COMMERCIAL.PIPELINE_SETTINGS.TYPES.LOST') }}
                    </option>
                  </select></label
                >
                <label class="flex flex-col gap-1.5 text-sm text-n-slate-11"
                  >{{ t('COMMERCIAL.PIPELINE_SETTINGS.FIELDS.COLOR')
                  }}<input
                    v-model="stage.color"
                    type="color"
                    class="h-10 w-full cursor-pointer rounded-lg border border-n-weak bg-n-alpha-1 p-1"
                /></label>
                <label class="flex flex-col gap-1.5 text-sm text-n-slate-11"
                  >{{ t('COMMERCIAL.PIPELINE_SETTINGS.FIELDS.FOLLOW_UP_DAYS')
                  }}<input
                    v-model.number="stage.rules.followUpDays"
                    type="number"
                    min="0"
                    :placeholder="
                      t(
                        'COMMERCIAL.PIPELINE_SETTINGS.PLACEHOLDERS.FOLLOW_UP_DAYS'
                      )
                    "
                    class="h-10 rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-n-slate-12"
                /></label>
                <label class="flex flex-col gap-1.5 text-sm text-n-slate-11"
                  >{{
                    t(
                      'COMMERCIAL.PIPELINE_SETTINGS.FIELDS.INACTIVITY_ALERT_DAYS'
                    )
                  }}<input
                    v-model.number="stage.rules.inactivityAlertDays"
                    type="number"
                    min="0"
                    :placeholder="
                      t(
                        'COMMERCIAL.PIPELINE_SETTINGS.PLACEHOLDERS.INACTIVITY_ALERT_DAYS'
                      )
                    "
                    class="h-10 rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-n-slate-12"
                /></label>
                <label class="flex flex-col gap-1.5 text-sm text-n-slate-11"
                  >{{ t('COMMERCIAL.PIPELINE_SETTINGS.FIELDS.INTENT')
                  }}<input
                    v-model="stage.rules.intent"
                    :placeholder="
                      t('COMMERCIAL.PIPELINE_SETTINGS.PLACEHOLDERS.INTENT')
                    "
                    class="h-10 rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-n-slate-12"
                /></label>
                <label
                  class="flex flex-col gap-1.5 text-sm text-n-slate-11 lg:col-span-2"
                  >{{ t('COMMERCIAL.PIPELINE_SETTINGS.FIELDS.SUGGESTED_ACTION')
                  }}<textarea
                    v-model="stage.rules.suggestedAction"
                    rows="2"
                    :placeholder="
                      t(
                        'COMMERCIAL.PIPELINE_SETTINGS.PLACEHOLDERS.SUGGESTED_ACTION'
                      )
                    "
                    class="rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-n-slate-12"
                  />
                </label>
                <label class="flex flex-col gap-1.5 text-sm text-n-slate-11"
                  >{{ t('COMMERCIAL.PIPELINE_SETTINGS.FIELDS.AI_LABEL')
                  }}<input
                    v-model="stage.rules.aiLabel"
                    :placeholder="
                      t('COMMERCIAL.PIPELINE_SETTINGS.PLACEHOLDERS.AI_LABEL')
                    "
                    class="h-10 rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-n-slate-12"
                /></label>
              </div>
              <div
                class="mt-5 flex flex-wrap gap-x-6 gap-y-3 border-t border-n-weak pt-4"
              >
                <label
                  v-for="field in toggleFields"
                  :key="field.key"
                  class="flex cursor-pointer items-center gap-2 text-sm text-n-slate-11"
                  ><input
                    v-model="stage[field.key]"
                    type="checkbox"
                    class="size-4 rounded border-n-weak text-n-brand"
                  />{{ field.label }}</label
                >
              </div>
            </div>
          </article>
        </template>
      </Draggable>

      <section v-if="archivedStages.length" class="mt-8">
        <h2 class="mb-3 text-base font-semibold text-n-slate-12">
          {{ t('COMMERCIAL.PIPELINE_SETTINGS.ARCHIVED_TITLE') }}
        </h2>
        <div class="space-y-2">
          <article
            v-for="stage in archivedStages"
            :key="stageKey(stage)"
            class="flex items-center gap-3 rounded-xl border border-dashed border-n-weak bg-n-alpha-1 p-4 opacity-80"
          >
            <span class="i-lucide-archive size-4 text-n-slate-9" />
            <div class="min-w-0 flex-1">
              <p class="truncate font-medium">{{ stage.name }}</p>
              <p class="text-xs text-n-slate-9">
                {{ stageTypeLabel(stage.stageType) }}
              </p>
            </div>
            <button
              type="button"
              class="rounded-lg border border-n-weak px-3 py-1.5 text-xs font-medium hover:bg-n-alpha-2"
              @click="restoreStage(stage)"
            >
              {{ t('COMMERCIAL.PIPELINE_SETTINGS.RESTORE') }}
            </button>
          </article>
        </div>
      </section>

      <div
        v-if="archiveCandidate"
        class="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
        @click.self="archiveCandidate = null"
      >
        <section
          class="w-full max-w-md rounded-2xl border border-n-weak bg-n-solid-2 p-5 shadow-xl"
        >
          <h2 class="text-lg font-semibold">
            {{ t('COMMERCIAL.PIPELINE_SETTINGS.ARCHIVE_DIALOG.TITLE') }}
          </h2>
          <p class="mt-2 text-sm text-n-slate-10">
            {{
              t('COMMERCIAL.PIPELINE_SETTINGS.ARCHIVE_DIALOG.DESCRIPTION', {
                name: archiveCandidate.name,
              })
            }}
          </p>
          <label
            v-if="archiveCandidate.dealsCount"
            class="mt-4 block text-sm text-n-slate-11"
            >{{ t('COMMERCIAL.PIPELINE_SETTINGS.ARCHIVE_DIALOG.REPLACEMENT')
            }}<select
              v-model="replacementStageId"
              class="mt-1.5 h-10 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3"
            >
              <option value="">
                {{ t('COMMERCIAL.PIPELINE_SETTINGS.ARCHIVE_DIALOG.SELECT') }}
              </option>
              <option
                v-for="stage in replacementOptions"
                :key="stageKey(stage)"
                :value="stage.id"
              >
                {{ stage.name }}
              </option></select
            ><span
              v-if="!replacementOptions.length"
              class="mt-2 block text-xs text-n-ruby-10"
              >{{
                t('COMMERCIAL.PIPELINE_SETTINGS.ARCHIVE_DIALOG.NO_REPLACEMENT')
              }}</span
            ></label
          >
          <div class="mt-5 flex justify-end gap-2">
            <button
              type="button"
              class="rounded-lg border border-n-weak px-3 py-2 text-sm"
              @click="archiveCandidate = null"
            >
              {{ t('COMMERCIAL.PIPELINE_SETTINGS.CANCEL') }}</button
            ><button
              type="button"
              class="rounded-lg bg-n-ruby-9 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
              :disabled="archiveCandidate.dealsCount && !replacementStageId"
              @click="confirmArchive"
            >
              {{ t('COMMERCIAL.PIPELINE_SETTINGS.ARCHIVE') }}
            </button>
          </div>
        </section>
      </div>

      <div
        v-if="templateConfirmOpen"
        class="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
        @click.self="templateConfirmOpen = false"
      >
        <section
          class="w-full max-w-md rounded-2xl border border-n-weak bg-n-solid-2 p-5 shadow-xl"
        >
          <h2 class="text-lg font-semibold">
            {{ t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.CONFIRM_TITLE') }}
          </h2>
          <p class="mt-2 text-sm text-n-slate-10">
            {{ t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.CONFIRM') }}
          </p>
          <div class="mt-5 flex justify-end gap-2">
            <button
              type="button"
              class="rounded-lg border border-n-weak px-3 py-2 text-sm"
              @click="templateConfirmOpen = false"
            >
              {{ t('COMMERCIAL.PIPELINE_SETTINGS.CANCEL') }}
            </button>
            <button
              type="button"
              class="rounded-lg bg-n-brand px-3 py-2 text-sm font-medium text-white"
              @click="applyTemplate"
            >
              {{ t('COMMERCIAL.PIPELINE_SETTINGS.TEMPLATES.APPLY') }}
            </button>
          </div>
        </section>
      </div>
    </template>
  </SettingsLayout>
</template>
